import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import FloatingArrow from './FloatingArrow';
import * as styles from './FloatingCard.module.scss';
import { useTetherContext } from './Tether';

interface FloatingCardProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const FloatingCard = forwardRef<HTMLDivElement, FloatingCardProps>(
  ({ children, ...props }, ref) => {
    const { setArrow, arrowData, placement } = useTetherContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;

      const observer = new ResizeObserver((entries) => {
        const hasLoaded = entries.some(
          (entry) => entry.contentRect.width * entry.contentRect.height > 0,
        );
        if (hasLoaded) {
          observer.disconnect();
          setIsLoading(false);
        }
      });

      observer.observe(container);
      return () => observer.disconnect();
    }, []);

    return (
      <Slot ref={containerRef} {...props}>
        <div
          ref={ref}
          className={classNames(styles.container, {
            [styles.isLoading]: isLoading,
          })}
        >
          {children}
          <div className={styles.safeHoverArea} data-placement={placement} />
          <FloatingArrow
            ref={setArrow}
            placement={placement}
            arrowData={arrowData}
          />
        </div>
      </Slot>
    );
  },
);

export default FloatingCard;
